import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const en = {
  common: {
    open: "Open", duplicate: "Duplicate", delete: "Delete", cancel: "Cancel", save: "Save",
    saving: "Saving...", loading: "Loading...", search: "Search", noPreview: "No Preview",
    optional: "optional", back: "Back", add: "Add", edit: "Edit", close: "Close", apply: "Apply",
    confirm: "Confirm", yes: "Yes", no: "No", chooseFile: "Choose file", processing: "Processing...",
    justNow: "just now", minutesAgo: "{{count}}m ago", hoursAgo: "{{count}}h ago", daysAgo: "{{count}}d ago",
    untitled: "Untitled", noCustomer: "No customer",
    readOnly: "Read only", adminOnly: "Admin only",
  },
  language: { label: "Language", en: "English", zh: "中文", switch: "Switch language" },
  nav: {
    home: "Home", products: "Products", brands: "Brands", templates: "Templates",
    designer: "Color Card Design", standardCards: "Standard Card Library", batch: "Batch Generation",
    drafts: "Drafts", settings: "Settings", logout: "Log out", appName: "Color Card Assistant",
    help: "Help",
  },
  help: {
    pageTitle: "Help & User Guide", downloadPdf: "Download PDF", downloading: "Preparing...",
    intro: "Full Chinese user guide for the BHF Color Card Assistant. Download the PDF for offline reading or sharing.",
    loadError: "Failed to load help content.", downloadError: "Failed to download PDF.",
  },
  login: {
    heroTitle: "Color Card Assistant",
    heroDesc: "Generate and edit BHF color cards in minutes — pick a product, template and brand, or upload an existing card to make quick revisions.",
    footer: "Internal Design Tool · BHF Home Textiles",
    signIn: "Sign in", subtitle: "Access the Color Card Assistant workspace.",
    email: "Email", password: "Password", signingIn: "Signing in...",
    name: "Name", namePlaceholder: "Your name", register: "Create account", registering: "Creating...",
    createAccount: "Create an account", registerSubtitle: "Sign up to start creating color cards.",
    noAccount: "Don't have an account?", haveAccount: "Already have an account?", signUp: "Sign up", backToSignIn: "Sign in",
    passwordMin: "Password must be at least 6 characters",
  },
  home: {
    title: "BHF Color Card Assistant",
    subtitle: "Generate new color cards or revise existing ones in a few clicks.",
    createTitle: "Create New Color Card", createDesc1: "Product + Template + Brand", createDesc2: "Auto-generate a color card",
    startNew: "Start New Design",
    editTitle: "Edit Existing Color Card", editDesc1: "Upload an existing card", editDesc2: "and make quick revisions",
    uploadExisting: "Upload Existing Card",
    batchTitle: "Batch Generation", batchDesc1: "One sales contract,", batchDesc2: "generate every card in one click",
    startBatch: "Start Batch Generation",
    recent: "Recent Designs", noRecent: "No designs yet. Start your first color card above.",
    duplicatedAs: "Duplicated as {{n}}", duplicateFailed: "Failed to duplicate design", untitledProduct: "Untitled Product",
  },
  drafts: {
    title: "Drafts & Designs", searchPlaceholder: "Search design number / customer", none: "No designs found.",
  },
  upload: {
    backHome: "Back to Home", title: "Edit Existing Color Card",
    subtitle: "Upload an existing card and make quick revisions on top of it.",
    customer: "Customer", customerPlaceholder: "e.g. Walmart",
    svgNote: "Best Choice · Editable", pdfNote: "Recommended for Existing Print Files", imageNote: "Use as Locked Background",
    hint: "AI / PSD files are not supported yet. If you have an AI file, convert it to SVG or PDF first; for PSD, convert to PDF or PNG.",
    loadedAs: "Loaded as {{n}}", uploadFailed: "Upload failed. Please try a different file.", processingUpload: "Processing upload...",
  },
  settings: {
    title: "Settings", account: "Account", name: "Name", email: "Email", role: "Role",
    fonts: "Fonts", fontsDesc: "Import .ttf / .otf fonts. Once imported they can be chosen in the designer's font dropdown, and both the preview and exported PNG/PDF will use them.",
    importFont: "Import Font", importing: "Importing...", noFonts: "No fonts imported yet",
    onlyTtfOtf: "Only .ttf or .otf font files are supported",
    fontImported: "Font \"{{family}}\" imported", fontDeleted: "Font \"{{family}}\" deleted",
    importFailed: "Font import failed", deleteFailed: "Delete failed",
    about: "About",
    aboutText: "BHF Color Card Assistant — internal MVP for generating and revising home textile color cards. Page sizes use millimeters, exports support PNG and PDF, and all rendering is SVG-first.",
    users: "User Management", usersDesc: "Create, edit and remove users. Admins have full access; users can only edit color cards they created.",
    addUser: "Add User", uName: "Name", uEmail: "Email", uRole: "Role", uPassword: "Password",
    uPasswordOpt: "Password (leave blank to keep)", roleAdmin: "Administration", roleUser: "User",
    uActions: "Actions", createUserTitle: "Add User", editUserTitle: "Edit User",
    userCreated: "User created", userUpdated: "User updated", userDeleted: "User deleted",
    userSaveFailed: "Failed to save user", userDeleteFailed: "Failed to delete user",
    deleteUserTitle: "Delete user?", deleteUserDesc: "This will permanently remove \"{{email}}\".",
    you: "You", noUsers: "No users yet",
  },
  products: {
    title: "Products", add: "Add Product", addTitle: "Add Product", editTitle: "Edit Product",
    searchPlaceholder: "Search SKU / Product Name", mainImage: "Main Image", noImage: "No Image",
    sku: "SKU", name: "Product Name", category: "Category", status: "Status", active: "Active", inactive: "Inactive",
    size: "Size", material: "Material", pieces: "Pieces", upc: "UPC", description: "Description", care: "Care Instructions",
    deleteTitle: "Delete product?", deleteDesc: "This will permanently remove \"{{name}}\".",
    created: "Product created", updated: "Product updated", saveFailed: "Failed to save product",
    deleted: "Product deleted", deleteFailed: "Failed to delete product", imageUploadFailed: "Image upload failed",
  },
  brands: {
    title: "Brands", add: "Add Brand", searchPlaceholder: "Search brand name / code",
    name: "Brand Name", code: "Brand Code", createConfigure: "Create & Configure",
    nameRequired: "Brand name and code are required", created: "Brand created", createFailed: "Failed to create brand",
  },
  brandDetail: {
    back: "Back to Brands", logos: "Logos", noLogo: "No logo", removeWhiteBg: "Remove white bg",
    whiteBgRemoved: "White background removed", removeBgFailed: "Failed to remove background",
    logoUpdated: "Logo updated", uploadFailed: "Upload failed", bgUpdated: "Background updated", saveFailed: "Failed to save",
    fonts: "Fonts", titleFont: "Title Font", bodyFont: "Body Font", colors: "Colors",
    backgrounds: "Backgrounds", bgHint: "Used automatically based on product category. Falls back to Default when empty.",
    image: "Image", main: "Main", white: "White", black: "Black",
    default: "Default", bedding: "Bedding", curtain: "Curtain", tableLinen: "Table Linen", placemat: "Placemat", throw: "Throw",
  },
  templates: {
    title: "Templates", add: "Add Template", addTitle: "Add Template", searchPlaceholder: "Search template code / name",
    code: "Template Code", name: "Template Name", category: "Category", orientation: "Orientation",
    landscape: "Landscape", portrait: "Portrait", width: "Width (mm)", height: "Height (mm)", bleed: "Bleed (mm)",
    create: "Create", created: "Template created", createFailed: "Failed to create template", codeNameRequired: "Template code and name are required",
  },
  templateDetail: {
    back: "Back to Templates", saveFailed: "Failed to save",
    pageSetup: "Page Setup", width: "Width (mm)", height: "Height (mm)", bleed: "Bleed (mm)",
    templateSvg: "Template SVG (optional decorative base)", svgPlaceholder: "Optional raw SVG fragment",
    elementConfig: "Element Config (JSON)",
    elementConfigHint: "Defines x / y / width / height (mm) for logo, productImage, productTitle, size, material, pieces, description, upc.",
    saveElementConfig: "Save Element Config", elementConfigSaved: "elementConfig saved (version bumped)", invalidJson: "Invalid JSON: ",
  },
  elements: {
    title: "Elements", background: "Background", addedText: "Added Text {{n}}", addedImage: "Added Image {{n}}",
    addText: "Add Text", addImage: "Add Image", aiDetect: "AI Detect Text", aiDetecting: "AI detecting...",
    drawLogo: "Draw Logo", drawing: "Drawing…",
    ovText: "Text", ovImage: "Image", ovLogo: "Logo", ovUpc: "UPC", ovCover: "Cover Block",
  },
  designer: {
    generatingCard: "Generating color card...",
    title: "Color Card Designer", modeANew: "MODE A · Create New", modeBEdit: "MODE B · Edit Existing",
    searchProduct: "Search product...", searchTemplate: "Search template...", noMatch: "No matches",
    customCanvas: "+ Custom canvas size", customCanvasTitle: "Custom Canvas Size", canvasName: "Name", canvasNamePh: "e.g. Custom 100x150",
    cw: "Width (mm)", ch: "Height (mm)", cbleed: "Bleed (mm)", createCanvas: "Create Canvas", canvasCreated: "Custom canvas created", canvasFailed: "Failed to create canvas",
    export: "Export", exportPng: "Export PNG", exportPdf: "Export PDF", saveDraft: "Save Draft", saveVersion: "Save as New Version",
    spellCheck: "Spell Check", spellChecking: "Checking...",
    product: "Product", template: "Template", brand: "Brand", version: "Version",
    customer: "Customer", customerPlaceholder: "e.g. Walmart", fit: "Fit",
    pageSize: "Page Size: {{w}} × {{h}} mm", bleed: "Bleed: {{b}} mm", showSafeArea: "Show Safe Area",
    rotateLeft: "Rotate 90° left", rotateRight: "Rotate 90° right",
    generateFailed: "Failed to generate color card", autosaveFailed: "Autosave failed",
    bgReplaced: "Background replaced", bgReplaceFailed: "Failed to replace background",
    draftSaved: "Draft saved", saveDraftFailed: "Failed to save draft",
    versionSaved: "Saved as new version", saveVersionFailed: "Failed to save new version",
    exportFailed: "Export failed", noTextToCheck: "No text to check",
    spellFound: "Found {{n}} possible spelling issue(s)", spellNone: "No spelling issues found", spellFailed: "Spell check failed, please retry",
    generatedAs: "Generated {{n}}", savedAs: "Saved as {{n}} (V{{v}})",
    aiLogoDetected: "Detected as Logo — replace or delete it on the right", aiLogoFailed: "Logo detection failed, please retry",
    aiTextDetected: "AI detected the text — please review and fine-tune the overlay and text positions", aiTextFailed: "AI detection failed, please retry",
    standardBadge: "Standard Card", sourceFile: "Source File", fileType: "File Type",
    drawLogoHint: "Drag to draw the logo area (Esc to cancel)",
    rotationHint: "Rotated view — select via the element list on the left and edit on the right; restore to 0° before dragging",
  },
  props: {
    selectElement: "Select an element to edit its properties",
    font: "Font", brandBackgrounds: "Brand Backgrounds", useAutoBg: "Use automatic background",
    logoVariant: "Logo Variant", upcNumber: "UPC Number", textContent: "Text Content", fontSize: "Font Size",
    clearColorUseImage: "Clear color, use image background",
    style: "Style", color: "Color", coverColor: "Cover Color",
    pantoneCode: "Pantone Code", customColor: "Custom Color", quickSwatches: "Quick Swatches",
    pantonePlaceholder: "e.g. 19-4052 or #1B2A4A", pantoneNote: "Approximate color values, not official Pantone-licensed data",
    replace: "Replace", replaceBackground: "Replace Background", replaceLogo: "Replace Logo", replaceImage: "Replace Image",
    remove: "Remove", removeWhiteBg: "Remove White Bg", rotate180: "Rotate 180°", rotated180: "Rotated 180° (click to reset)",
    logoNote: "The white background is auto-removed on upload and the logo is scaled to fit its box by aspect ratio. If the result isn't ideal, click \"Remove White Bg\" to reprocess.",
    tBackground: "Background", tLogo: "Logo", tProductImage: "Product Image", tUpc: "UPC", tAddedText: "Added Text", tAddedImage: "Added Image",
    whiteBgRemoved: "White background removed", removeBgFailed: "Failed to remove background, please retry",
    onlyTtfOtf: "Only .ttf or .otf font files are supported", fontImportedApplied: "Font \"{{family}}\" imported and applied", fontImportFailed: "Font import failed",
    spellSuggest: "Spelling suggestions", spellReplaceAll: "Replace all ({{n}})",
    fldProductTitle: "Product Title", fldSize: "Size", fldMaterial: "Material", fldPieces: "Pieces", fldDescription: "Description",
  },
  std: {
    title: "Standard Card Library", subtitle: "Manage reusable standard color card designs by product category and brand.",
    addCard: "New Standard Card", searchPlaceholder: "Search by code, e.g. SC-BED-CRH-001",
    categoryPh: "Category", allCategories: "All categories", brandPh: "Brand", allBrands: "All brands",
    editDesign: "Edit Design", deleteTitle: "Delete standard card?", deleteDesc: "This will permanently remove code \"{{code}}\".",
    noCards: "No standard cards yet. Click \"New Standard Card\" to create one.",
    createTitle: "New Standard Card", createDesc: "Pick a product, template and brand — you'll enter the designer to customize after creating.",
    code: "Standard Card Code", product: "Product", selectProduct: "Select product", template: "Template", selectTemplate: "Select template",
    brand: "Brand", selectBrand: "Select brand", category: "Category", selectCategory: "Select category",
    creating: "Creating...", createSubmit: "Create & open designer", editTitle: "Edit Standard Card Info", editDesc: "Change this standard card's code or category.",
    loadFailed: "Failed to load the standard card library", codeRequired: "Please enter a standard card code", selectPTB: "Please select product, template and brand",
    created: "Standard card {{code}} created", codeEmpty: "Code cannot be empty", updated: "Updated", deleted: "Deleted", deleteFailed: "Delete failed",
  },
  batch: {
    title: "Batch Color Card Generation", subtitle: "Enter a sales contract number to generate the whole batch in one click.",
    contractPlaceholder: "Enter contract number, e.g. BH-2026-0815", loadOrder: "Load Order", loading: "Loading...",
    contractList: "Contract List", edit: "Edit", noContracts: "No contract records yet",
    itemsCount: "{{n}} products", genAll: "Generate All", generating: "Generating...", approveAll: "Approve All",
    downloadZip: "Download ZIP", packing: "Packing...", completed: "Done {{done}}/{{total}}",
    linkStd: "+ Link Standard Card", fixInfo: "Fix Info", approve: "Approve", noItems: "This order has no products yet",
    fixTitle: "Complete Product Info · {{sku}}", fixDesc: "Fill in the missing info below; the product status updates automatically after saving.",
    stdCodeLabel: "Standard Card Code (if set, generation reuses that standard card and only swaps logo/font)", stdCodePlaceholder: "e.g. SC-BED-CRH-001, leave empty to skip",
    templateLabel: "Card Template", selectTemplate: "Select template", brandLabel: "Brand", selectBrand: "Select brand",
    upcLabel: "UPC Barcode", upcPlaceholder: "Enter UPC code", logoUrlLabel: "Customer Logo URL", logoUrlPlaceholder: "Enter logo image URL",
    settingsTitle: "Contract Settings · {{n}}", settingsDesc: "These settings only affect cards newly generated for this contract afterwards; already-generated cards are unaffected (unless regenerated).",
    custLogoLabel: "Customer Logo (overrides the brand logo for all products in this contract)", uploadLogo: "Upload Logo", uploading: "Uploading...", removeWhiteBg: "Remove White Bg", processing: "Processing...",
    logoNote: "The white background is auto-removed on upload; if the result isn't ideal, click \"Remove White Bg\" to reprocess.",
    titleFontSize: "Title Font Size (pt)", egTitle: "e.g. 28", titleFontType: "Title Font Type", bodyFontSize: "Body Font Size (pt)", egBody: "e.g. 14", bodyFontType: "Body Font Type",
    regenTitle: "Regenerate all cards to apply the new settings?", regenDesc: "Existing cards for this contract were generated with the old logo/font settings and won't auto-update. Regenerate all products now to apply the settings you just saved? Approved cards will need re-approval after regeneration.",
    regenNo: "Not now", regenYes: "Regenerate All",
    enterContract: "Please enter a contract number", loaded: "Loaded contract {{n}} ({{c}} products)", loadFailed: "Failed to load this contract",
    logoUploadFailed: "Logo upload failed", whiteBgRemoved: "White background removed", removeBgFailed: "Failed to remove background",
    settingsSaved: "Settings saved — will apply to cards generated afterwards", genStarted: "Batch generation started, please wait...",
    regenStarted: "Batch regeneration started; the new logo/font settings will apply to all products...", regenerated: "Regenerated", approved: "Approved", allApproved: "All approved", downloadFailed: "Download failed", infoUpdated: "Info updated",
    save: "Save", saving: "Saving...", cancel: "Cancel",
    thContract: "Contract #", thCustomer: "Customer", thDate: "Date", thStatus: "Status", thActions: "Actions", thProduct: "Product", thCategory: "Category", thStdCode: "Standard Card Code", thMissing: "Missing Info",
    miss: { product: "Product", template: "Template", brand: "Brand", logo: "Customer Logo", upc: "UPC Barcode", productImage: "Product Image", productNameEN: "English Name", standardCard: "Invalid standard card code" },
    font: { none: "No override (use default)", sansSerif: "Sans-serif", serif: "Serif", monospace: "Monospace", condensed: "Condensed" },
    status: { Pending: "Pending", "Needs Review": "Needs Review", Generating: "Generating", Generated: "Generated", Edited: "Edited", Approved: "Approved", Failed: "Failed", Draft: "Draft", Completed: "Completed", Partial: "Partial" },
  },
};

const zh = {
  common: {
    open: "打开", duplicate: "复制", delete: "删除", cancel: "取消", save: "保存",
    saving: "保存中...", loading: "加载中...", search: "搜索", noPreview: "无预览",
    optional: "可选", back: "返回", add: "添加", edit: "编辑", close: "关闭", apply: "应用",
    confirm: "确认", yes: "是", no: "否", chooseFile: "选择文件", processing: "处理中...",
    justNow: "刚刚", minutesAgo: "{{count}} 分钟前", hoursAgo: "{{count}} 小时前", daysAgo: "{{count}} 天前",
    untitled: "未命名", noCustomer: "无客户",
    readOnly: "只读", adminOnly: "仅管理员",
  },
  language: { label: "语言", en: "English", zh: "中文", switch: "切换语言" },
  nav: {
    home: "首页", products: "产品", brands: "品牌", templates: "模板",
    designer: "彩卡设计", standardCards: "彩卡标准库", batch: "批量彩卡生成",
    drafts: "草稿", settings: "设置", logout: "退出登录", appName: "彩卡助手",
    help: "帮助",
  },
  help: {
    pageTitle: "帮助中心 · 使用手册", downloadPdf: "下载 PDF", downloading: "生成中...",
    intro: "BHF 彩卡助手完整中文使用指南。可下载 PDF 以便离线阅读或分享给同事。",
    loadError: "加载帮助内容失败。", downloadError: "下载 PDF 失败。",
  },
  login: {
    heroTitle: "彩卡助手",
    heroDesc: "几分钟内生成和编辑 BHF 彩卡——选择产品、模板和品牌，或上传现有彩卡进行快速修改。",
    footer: "内部设计工具 · BHF 家纺",
    signIn: "登录", subtitle: "进入彩卡助手工作台。",
    email: "邮箱", password: "密码", signingIn: "登录中...",
    name: "姓名", namePlaceholder: "您的姓名", register: "注册账号", registering: "注册中...",
    createAccount: "创建账号", registerSubtitle: "注册后即可开始创建彩卡。",
    noAccount: "还没有账号？", haveAccount: "已有账号？", signUp: "注册", backToSignIn: "去登录",
    passwordMin: "密码至少需要 6 位",
  },
  home: {
    title: "BHF 彩卡助手",
    subtitle: "几步点击即可生成新彩卡或修改现有彩卡。",
    createTitle: "创建新彩卡", createDesc1: "产品 + 模板 + 品牌", createDesc2: "自动生成彩卡",
    startNew: "开始新设计",
    editTitle: "编辑现有彩卡", editDesc1: "上传现有彩卡", editDesc2: "进行简单修改",
    uploadExisting: "上传现有彩卡",
    batchTitle: "批量彩卡生成", batchDesc1: "一个合同订单", batchDesc2: "一键生成全部彩卡",
    startBatch: "开始批量生成",
    recent: "最近设计", noRecent: "还没有设计。在上方开始你的第一张彩卡。",
    duplicatedAs: "已复制为 {{n}}", duplicateFailed: "复制设计失败", untitledProduct: "未命名产品",
  },
  drafts: {
    title: "草稿与设计", searchPlaceholder: "搜索设计编号 / 客户", none: "未找到设计。",
  },
  upload: {
    backHome: "返回首页", title: "编辑现有彩卡",
    subtitle: "上传现有彩卡并在其上进行快速修改。",
    customer: "客户", customerPlaceholder: "例如 Walmart",
    svgNote: "最佳选择 · 可编辑", pdfNote: "适合现有印刷文件", imageNote: "作为锁定背景使用",
    hint: "暂时不支持 AI / PSD 文件。如果您有 AI 文件，建议先转 SVG 或 PDF；如果您有 PSD 文件，建议先转 PDF 或 PNG。",
    loadedAs: "已载入为 {{n}}", uploadFailed: "上传失败，请更换文件重试。", processingUpload: "正在处理上传...",
  },
  settings: {
    title: "设置", account: "账户", name: "姓名", email: "邮箱", role: "角色",
    fonts: "字体", fontsDesc: "导入 .ttf / .otf 字体，导入后可在设计器右侧的字体下拉中选用，预览与导出的 PNG/PDF 都会使用该字体。",
    importFont: "导入字体", importing: "导入中...", noFonts: "还没有导入字体",
    onlyTtfOtf: "只支持 .ttf 或 .otf 字体文件",
    fontImported: "字体「{{family}}」导入成功", fontDeleted: "已删除字体「{{family}}」",
    importFailed: "字体导入失败", deleteFailed: "删除失败",
    about: "关于",
    aboutText: "BHF 彩卡助手——用于生成和修改家纺彩卡的内部 MVP。页面尺寸以毫米为单位，导出支持 PNG 和 PDF，所有渲染以 SVG 优先。",
    users: "用户管理", usersDesc: "创建、编辑和删除用户。管理员拥有全部权限；普通用户只能编辑自己创建的彩卡。",
    addUser: "添加用户", uName: "姓名", uEmail: "邮箱", uRole: "角色", uPassword: "密码",
    uPasswordOpt: "密码（留空则不修改）", roleAdmin: "管理员", roleUser: "普通用户",
    uActions: "操作", createUserTitle: "添加用户", editUserTitle: "编辑用户",
    userCreated: "用户已创建", userUpdated: "用户已更新", userDeleted: "用户已删除",
    userSaveFailed: "保存用户失败", userDeleteFailed: "删除用户失败",
    deleteUserTitle: "删除用户？", deleteUserDesc: "此操作将永久删除「{{email}}」。",
    you: "当前账号", noUsers: "暂无用户",
  },
  products: {
    title: "产品", add: "添加产品", addTitle: "添加产品", editTitle: "编辑产品",
    searchPlaceholder: "搜索 SKU / 产品名称", mainImage: "主图", noImage: "无图片",
    sku: "SKU", name: "产品名称", category: "类别", status: "状态", active: "启用", inactive: "停用",
    size: "尺寸", material: "材质", pieces: "件数", upc: "UPC", description: "描述", care: "洗涤说明",
    deleteTitle: "删除产品？", deleteDesc: "此操作将永久删除「{{name}}」。",
    created: "产品已创建", updated: "产品已更新", saveFailed: "保存产品失败",
    deleted: "产品已删除", deleteFailed: "删除产品失败", imageUploadFailed: "图片上传失败",
  },
  brands: {
    title: "品牌", add: "添加品牌", searchPlaceholder: "搜索品牌名称 / 代码",
    name: "品牌名称", code: "品牌代码", createConfigure: "创建并配置",
    nameRequired: "品牌名称和代码为必填项", created: "品牌已创建", createFailed: "创建品牌失败",
  },
  brandDetail: {
    back: "返回品牌", logos: "Logo", noLogo: "无 Logo", removeWhiteBg: "去除白底",
    whiteBgRemoved: "已去除白色背景", removeBgFailed: "去除背景失败",
    logoUpdated: "Logo 已更新", uploadFailed: "上传失败", bgUpdated: "背景已更新", saveFailed: "保存失败",
    fonts: "字体", titleFont: "标题字体", bodyFont: "正文字体", colors: "颜色",
    backgrounds: "背景", bgHint: "根据产品类别自动使用，留空时回退到默认。",
    image: "图片", main: "主色", white: "白色", black: "黑色",
    default: "默认", bedding: "床品", curtain: "窗帘", tableLinen: "桌布", placemat: "餐垫", throw: "毯子",
  },
  templates: {
    title: "模板", add: "添加模板", addTitle: "添加模板", searchPlaceholder: "搜索模板代码 / 名称",
    code: "模板代码", name: "模板名称", category: "类别", orientation: "方向",
    landscape: "横向", portrait: "纵向", width: "宽度 (mm)", height: "高度 (mm)", bleed: "出血 (mm)",
    create: "创建", created: "模板已创建", createFailed: "创建模板失败", codeNameRequired: "模板代码和名称为必填项",
  },
  templateDetail: {
    back: "返回模板", saveFailed: "保存失败",
    pageSetup: "页面设置", width: "宽度 (mm)", height: "高度 (mm)", bleed: "出血 (mm)",
    templateSvg: "模板 SVG（可选装饰底图）", svgPlaceholder: "可选的原始 SVG 片段",
    elementConfig: "元素配置 (JSON)",
    elementConfigHint: "定义 logo、productImage、productTitle、size、material、pieces、description、upc 的 x / y / 宽 / 高（mm）。",
    saveElementConfig: "保存元素配置", elementConfigSaved: "元素配置已保存（版本号+1）", invalidJson: "JSON 无效：",
  },
  elements: {
    title: "元素", background: "背景", addedText: "新增文字 {{n}}", addedImage: "新增图片 {{n}}",
    addText: "添加文字", addImage: "添加图片", aiDetect: "AI 自动识别文字", aiDetecting: "AI 识别中...",
    drawLogo: "画 Logo", drawing: "画框中…",
    ovText: "文字", ovImage: "图片", ovLogo: "Logo", ovUpc: "UPC", ovCover: "覆盖块",
  },
  designer: {
    generatingCard: "正在生成彩卡...",
    title: "彩卡设计器", modeANew: "模式 A · 新建", modeBEdit: "模式 B · 编辑现有",
    searchProduct: "搜索产品...", searchTemplate: "搜索模板...", noMatch: "无匹配结果",
    customCanvas: "+ 自定义画布尺寸", customCanvasTitle: "自定义画布尺寸", canvasName: "名称", canvasNamePh: "例如 自定义 100x150",
    cw: "宽度 (mm)", ch: "高度 (mm)", cbleed: "出血 (mm)", createCanvas: "创建画布", canvasCreated: "已创建自定义画布", canvasFailed: "创建画布失败",
    export: "导出", exportPng: "导出 PNG", exportPdf: "导出 PDF", saveDraft: "保存草稿", saveVersion: "另存为新版本",
    spellCheck: "拼写检查", spellChecking: "检查中...",
    product: "产品", template: "模板", brand: "品牌", version: "版本",
    customer: "客户", customerPlaceholder: "例如 Walmart", fit: "适配",
    pageSize: "页面尺寸：{{w}} × {{h}} mm", bleed: "出血：{{b}} mm", showSafeArea: "显示安全区",
    rotateLeft: "向左旋转90°", rotateRight: "向右旋转90°",
    generateFailed: "生成彩卡失败", autosaveFailed: "自动保存失败",
    bgReplaced: "背景已替换", bgReplaceFailed: "替换背景失败",
    draftSaved: "草稿已保存", saveDraftFailed: "保存草稿失败",
    versionSaved: "已另存为新版本", saveVersionFailed: "保存新版本失败",
    exportFailed: "导出失败", noTextToCheck: "没有可检查的文字",
    spellFound: "发现 {{n}} 处可能的拼写问题", spellNone: "未发现拼写问题", spellFailed: "拼写检查失败，请重试",
    generatedAs: "已生成 {{n}}", savedAs: "已保存为 {{n}}（V{{v}}）",
    aiLogoDetected: "已识别为 Logo，可在右侧替换或删除", aiLogoFailed: "Logo 识别失败，请重试",
    aiTextDetected: "AI 已识别文字，请检查并微调覆盖块与文字位置", aiTextFailed: "AI 识别失败，请重试",
    standardBadge: "标准彩卡", sourceFile: "源文件", fileType: "文件类型",
    drawLogoHint: "拖拽绘制 Logo 区域（Esc 取消）",
    rotationHint: "旋转视图中，可通过左侧元素列表选中并在右侧编辑；拖拽调整请先恢复 0°",
  },
  props: {
    selectElement: "选择一个元素以编辑其属性",
    font: "字体", brandBackgrounds: "品牌背景", useAutoBg: "使用自动背景",
    logoVariant: "Logo 变体", upcNumber: "UPC 编号", textContent: "文字内容", fontSize: "字号",
    clearColorUseImage: "清除颜色，使用图片背景",
    style: "样式", color: "颜色", coverColor: "覆盖颜色",
    pantoneCode: "潘通色号 Pantone", customColor: "自由选择颜色", quickSwatches: "常用色块",
    pantonePlaceholder: "例如 19-4052 或 #1B2A4A", pantoneNote: "近似色值，非 Pantone 官方授权数据",
    replace: "替换", replaceBackground: "替换背景", replaceLogo: "替换 Logo", replaceImage: "替换图片",
    remove: "删除", removeWhiteBg: "去除白底", rotate180: "旋转180°", rotated180: "已旋转180°（点击还原）",
    logoNote: "上传时会自动去除白色背景并按图片比例自适应填满 Logo 区域，如效果不理想可点击\"去除白底\"重新处理",
    tBackground: "背景", tLogo: "Logo", tProductImage: "产品图片", tUpc: "UPC", tAddedText: "新增文字", tAddedImage: "新增图片",
    whiteBgRemoved: "已去除白色背景", removeBgFailed: "去除背景失败，请重试",
    onlyTtfOtf: "只支持 .ttf 或 .otf 字体文件", fontImportedApplied: "字体「{{family}}」已导入并应用", fontImportFailed: "字体导入失败",
    spellSuggest: "拼写建议", spellReplaceAll: "全部替换（{{n}}）",
    fldProductTitle: "产品标题", fldSize: "尺寸", fldMaterial: "材质", fldPieces: "件数", fldDescription: "描述",
  },
  std: {
    title: "彩卡标准库", subtitle: "按产品类别与品牌管理可复用的标准彩卡设计",
    addCard: "新建标准彩卡", searchPlaceholder: "按编号搜索，例如 SC-BED-CRH-001",
    categoryPh: "品类", allCategories: "全部品类", brandPh: "品牌", allBrands: "全部品牌",
    editDesign: "编辑设计", deleteTitle: "删除标准彩卡?", deleteDesc: "将永久删除编号「{{code}}」。",
    noCards: "暂无标准彩卡，点击\"新建标准彩卡\"开始创建。",
    createTitle: "新建标准彩卡", createDesc: "选择产品、模板与品牌，创建后将进入设计器进行内容定制。",
    code: "标准彩卡编号", product: "产品", selectProduct: "选择产品", template: "模板", selectTemplate: "选择模板",
    brand: "品牌", selectBrand: "选择品牌", category: "品类", selectCategory: "选择品类",
    creating: "创建中...", createSubmit: "创建并进入设计器", editTitle: "编辑标准彩卡信息", editDesc: "修改该标准彩卡的编号或所属品类。",
    loadFailed: "加载标准彩卡库失败", codeRequired: "请输入标准彩卡编号", selectPTB: "请选择产品、模板和品牌",
    created: "已创建标准彩卡 {{code}}", codeEmpty: "编号不能为空", updated: "已更新", deleted: "已删除", deleteFailed: "删除失败",
  },
  batch: {
    title: "批量彩卡生成", subtitle: "输入销售合同号，一键生成整批产品的彩卡",
    contractPlaceholder: "输入合同号，例如 BH-2026-0815", loadOrder: "加载订单", loading: "加载中...",
    contractList: "合同号列表", edit: "编辑", noContracts: "暂无合同记录",
    itemsCount: "{{n}} 个产品", genAll: "一键批量生成", generating: "生成中...", approveAll: "全部审批",
    downloadZip: "下载ZIP", packing: "打包中...", completed: "已完成 {{done}}/{{total}}",
    linkStd: "+ 关联标准彩卡", fixInfo: "补全信息", approve: "审批", noItems: "该订单暂无产品",
    fixTitle: "补全产品信息 · {{sku}}", fixDesc: "请填写下方缺失的信息，保存后系统会自动更新该产品的状态。",
    stdCodeLabel: "标准彩卡编号（如填写，生成时将直接复用该标准彩卡，只替换logo/字体）", stdCodePlaceholder: "例如 SC-BED-CRH-001，留空则不使用",
    templateLabel: "彩卡模板", selectTemplate: "选择模板", brandLabel: "品牌", selectBrand: "选择品牌",
    upcLabel: "UPC条码", upcPlaceholder: "输入UPC码", logoUrlLabel: "客户Logo URL", logoUrlPlaceholder: "输入Logo图片URL",
    settingsTitle: "合同设置 · {{n}}", settingsDesc: "这些设置仅影响该合同之后新生成的彩卡，已生成的彩卡不受影响（除非重新生成）。",
    custLogoLabel: "客户Logo（统一覆盖该合同下所有产品的品牌Logo）", uploadLogo: "上传Logo", uploading: "上传中...", removeWhiteBg: "去除白底", processing: "处理中...",
    logoNote: "上传时会自动尝试去除白色背景，如效果不理想可点击\"去除白底\"重新处理",
    titleFontSize: "标题字体大小 (pt)", egTitle: "例如 28", titleFontType: "标题字体类型", bodyFontSize: "普通文字字体大小 (pt)", egBody: "例如 14", bodyFontType: "普通文字字体类型",
    regenTitle: "是否重新生成全部彩卡以应用新设置？", regenDesc: "该合同下已有彩卡是基于旧的Logo/字体设置生成的，不会自动更新。是否立即批量重新生成全部产品的彩卡，以套用刚保存的新设置？已审批的彩卡重新生成后需要重新审批。",
    regenNo: "暂不需要", regenYes: "批量重新生成",
    enterContract: "请输入合同号", loaded: "已加载合同 {{n}}，共 {{c}} 个产品", loadFailed: "加载该合同失败",
    logoUploadFailed: "Logo上传失败", whiteBgRemoved: "已去除白色背景", removeBgFailed: "去除背景失败",
    settingsSaved: "设置已保存，将应用于之后新生成的彩卡", genStarted: "已开始批量生成，请稍候...",
    regenStarted: "已开始批量重新生成，新的Logo/字体设置将应用到全部产品...", regenerated: "已重新生成", approved: "已审批通过", allApproved: "已全部审批通过", downloadFailed: "下载失败", infoUpdated: "信息已更新",
    save: "保存", saving: "保存中...", cancel: "取消",
    thContract: "合同号", thCustomer: "客户名称", thDate: "日期", thStatus: "状态", thActions: "操作", thProduct: "产品名称", thCategory: "品类", thStdCode: "标准彩卡编号", thMissing: "缺失信息",
    miss: { product: "产品", template: "模板", brand: "品牌", logo: "客户Logo", upc: "UPC条码", productImage: "产品图片", productNameEN: "英文名称", standardCard: "标准彩卡编号无效" },
    font: { none: "不覆盖（使用默认）", sansSerif: "无衬线 Sans-serif", serif: "衬线 Serif", monospace: "等宽 Monospace", condensed: "紧凑无衬线 Condensed" },
    status: { Pending: "待处理", "Needs Review": "待补全", Generating: "生成中", Generated: "已生成", Edited: "已编辑", Approved: "已审批", Failed: "失败", Draft: "草稿", Completed: "已完成", Partial: "部分完成" },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, zh: { translation: zh } },
    fallbackLng: "en",
    supportedLngs: ["en", "zh"],
    load: "languageOnly",
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "bhf_lang",
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
  });

export default i18n;
